import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ScaleSets } from 'graphql-api/gql-api-interfaces';
import { combineLatest } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { loadProductSuccess, memoizeProductSizeData } from '../actions';
import { ProductExtendedVariant } from '../models';
import {
  selectExtendedVariants,
  selectPrepacks,
  selectScaleSets,
  selectSelectedScaleOrDefaultAvailable,
} from '../selectors';

@Injectable()
export class PreviewSizesCacheEffects {
  /** Dependencies */
  constructor(private actions$: Actions, private store: Store) {}

  public createCacheOnProductLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProductSuccess),
      switchMap(({ product }) => {
        return combineLatest([
          this.availableMaterials$(product.productId),
          this.selectedScale$,
          this.scaleSetKeys$,
          this.availableScaleSets$,
          this.availablePrepacks$,
        ]).pipe(
          take(1),
          map(
            ([
              materials,
              selectedScale,
              scaleSetKeys,
              availableScaleSets,
              prepacks,
            ]) =>
              memoizeProductSizeData({
                articleId: product.productId,
                brandId: product.brandId,
                materials,
                scaleSetKeys,
                availableScaleSets,
                selectedScale,
                prepacks,
              }),
          ),
        );
      }),
    ),
  );

  public selectedScale$ = this.store.pipe(
    select(selectSelectedScaleOrDefaultAvailable),
    map((selectedScale) => selectedScale || ''),
  );

  public availablePrepacks$ = this.store.pipe(select(selectPrepacks));

  public availableScaleSets$ = this.store.pipe(
    select(selectScaleSets),
    map((scaleSets): ScaleSets => scaleSets || { default: '', scales: {} }),
  );

  public scaleSetKeys$ = this.availableScaleSets$.pipe(
    map((scaleSets: ScaleSets) => Object.keys(scaleSets.scales).sort()),
  );

  public availableMaterials$ = (currentArticleId: string) =>
    this.store.pipe(
      select(selectExtendedVariants),
      map((materials): ProductExtendedVariant[] =>
        materials.filter(({ articleId }) => currentArticleId === articleId),
      ),
    );
}
